import React, { memo, useEffect, useMemo, useRef, useState } from 'react'

import { Formik } from 'formik'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { AANDenialReasons, LossReasonAndTypeOptions, subLossReasonsOptions } from '../options'
import {
  setAanReasons,
  setClosedLossDrawerMode,
  setClosedLossDrawerOpen,
  setClosedLossDrawerReason,
  setClosedLossDrawerSubReasonOtherText,
  setClosedLossDrawerSubReasons,
  setClosedLossDrawerType,
  setUAMDeclineEscalation,
} from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { InputField, MultiSelectField } from '@/components/FormikInputs'
import { SelectField } from '@/components/FormikInputs'
import Stepper from '@/components/Stepper'
import { graphqlApi } from '@/graphql'
import { useUpdateApplicationMutation, useUpdateSubStageMutation } from '@/services/application'
import { useUpdateCreditReportMutation } from '@/services/creditReport'
import { useUpdateEscalationMutation } from '@/services/escalations'
import { useUamDeclineMutation } from '@/services/streamingNote'
import { toastDefaultOptions } from '@/utils/toast'

const fieldIsRequiredMessage = 'Field is required'

const ClosedLost = ({ application, setActualState, modalCloseCallback, mode }) => {
  const dispatch = useAppDispatch()
  const closedLossDrawerReason = useAppSelector((state) => state.general.closedLossDrawerReason)
  const closedLossDrawerSubReasons = useAppSelector(
    (state) => state.general.closedLossDrawerSubReasons
  )
  const closedLossDrawerType = useAppSelector((state) => state.general.closedLossDrawerType)
  const closedLossDrawerSubReasonOtherText = useAppSelector(
    (state) => state.general.closedLossDrawerSubReasonOtherText
  )
  const escalation = useAppSelector((state) => state.general.UAMDeclineModalEscalation)
  const aanReasons = useAppSelector((state) => state.general.aanReasons)
  const formRef = useRef(null)
  const creditReport = application?.creditReports?.slice(-1).pop()
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [updateApplication] = useUpdateApplicationMutation()
  const [updateSubStage] = useUpdateSubStageMutation()
  const [updateCreditReport] = useUpdateCreditReportMutation()
  const [uamDecline] = useUamDeclineMutation()
  const [updateEscalation] = useUpdateEscalationMutation()

  const initialStep = useMemo(() => {
    if (
      closedLossDrawerReason &&
      closedLossDrawerSubReasons.length &&
      closedLossDrawerType &&
      (closedLossDrawerReason === 'credit' || mode === 'uam-decline')
    ) {
      return 2
    }
    if (closedLossDrawerReason && closedLossDrawerSubReasons.length && closedLossDrawerType) {
      return 1
    }
    if (closedLossDrawerType) {
      return 1
    }

    return 0
  }, [closedLossDrawerType, closedLossDrawerReason, closedLossDrawerSubReasons, mode])

  useEffect(() => {
    setTimeout(() => {
      formRef?.current?.validateForm()
    }, 0)
  }, [formRef?.current])

  const resetDrawer = (currentState, toastMessage) => {
    setMessage('')
    setIsError(false)
    dispatch(setClosedLossDrawerOpen(false))
    dispatch(setClosedLossDrawerType(''))
    dispatch(setClosedLossDrawerReason(''))
    dispatch(setClosedLossDrawerSubReasons([]))
    dispatch(setClosedLossDrawerSubReasonOtherText(''))
    dispatch(setClosedLossDrawerReason(''))
    dispatch(setUAMDeclineEscalation(null))
    dispatch(setClosedLossDrawerMode('closed-loss'))
    dispatch(setAanReasons([]))
    if (currentState === 'closed-lost') {
      toast.success(toastMessage, { ...toastDefaultOptions })
    } else toast.success(toastMessage, { ...toastDefaultOptions })
  }

  const handleFinishAction = (handleSubmit) => (_, __) => {
    handleSubmit()
  }

  const handleUpdateStage = async (values) => {
    try {
      const currentState = application?.applicationState

      const subLossReasons = values?.subLossReasons?.length
        ? values?.subLossReasons?.map((subReason) => {
            return { name: subReason?.value }
          })
        : []
      formRef?.current?.setSubmitting(true)
      const externalId = application?.externalId
        ? application?.externalId
        : application?.external_id
      await updateApplication({
        external_id: externalId,
        loss_type: values['lossType'],
        loss_reason: values['lossReason'],
        avm_decline: null,
        sub_loss_reasons: subLossReasons,
        sub_loss_reason_other_text: values?.otherText ? values?.otherText : null,
      })

      const response = await updateSubStage({
        external_id: externalId,
        application_state: 'closed-lost',
        aan_denial_reasons: values.selectedAANDenialReasons,
        loss_type: values['lossType'],
      })

      if ('data' in response && setActualState) {
        setActualState(response?.data?.application_state)
      }

      if (creditReport) {
        try {
          let payload: any = {
            external_id: creditReport?.externalId
              ? creditReport?.externalId
              : creditReport?.external_id,
            needs_to_be_declined: null,
          }
          if (values.selectedAANDenialReasons.length > 0) {
            payload = {
              ...payload,
              aan_denial_reason: values.selectedAANDenialReasons,
            }
          }
          await updateCreditReport({ payload, applicationUuid: application.uuid })
        } catch (error) {
          console.error(error)
        }
      }
      dispatch(graphqlApi.util.invalidateTags(['gql:application']))
      modalCloseCallback?.()

      let changeMessage = ''
      if (currentState === 'closed-lost') {
        changeMessage = 'The closed lost reason has changed successfully.'
      } else changeMessage = 'The stage has changed successfully.'

      setMessage(changeMessage)
      resetDrawer(currentState, changeMessage)
    } catch (error) {
      setIsError(true)
      setMessage('There was an error on the request, please try again')
    } finally {
      formRef?.current?.setSubmitting(false)
    }
  }

  const handleUamDecline = async (values, actions) => {
    const subLossReasons = values?.subLossReasons?.map((subReason) => subReason?.value)
    await uamDecline({
      application_id: application?.id,
      loss_reason: values?.lossReason,
      sub_loss_reasons: subLossReasons,
      uam_commentary: values?.uamCommentary,
      escalation_id: escalation?.id,
      other_text: values?.otherText ? values?.otherText : null,
    })

    if (escalation) {
      await updateEscalation({
        ...escalation,
        request_user: escalation?.request_user?.id as any,
        approved: false,
        uam_commentary: values?.uamCommentary,
      })
    }

    resetDrawer(application?.applicationState, 'The UAM Decline reason has changed successfully.')
  }

  const handleClosedLoss = async (values, actions) => {
    try {
      actions.setSubmitting(true)
      setIsError(false)
      await handleUpdateStage(values)
    } catch (error) {
      setIsError(true)
      setMessage('There was an error on the request, please try again')
      actions.setSubmitting(false)
    }
  }

  const getDefaultValues = () => {
    const options = Object.keys(AANDenialReasons).map((reason) => {
      return { value: reason, label: AANDenialReasons[reason] }
    })

    return options.filter((item) => aanReasons.includes(item.value))
  }

  const getContent = (values, setFieldValue, validateForm) => {
    const steps: any[] = [
      {
        title: 'Loss Type',
        content: () => (
          <SelectField
            style='default'
            label='Select loss type'
            name='lossType'
            extraClasses='w-full'
            options={LossReasonAndTypeOptions}
            onChange={(e) => {
              setFieldValue('lossType', e.target.value)
              setTimeout(() => {
                validateForm()
              }, 0)
            }}
            readOnly={mode === 'uam-decline'}
          />
        ),
        buttonLabel: 'Next',
        disabledButton: !values.lossType,
      },
      {
        title: 'Loss Reason and Sub-Reason',
        content: () => (
          <>
            <SelectField
              style='default'
              label='Select loss reason'
              name='lossReason'
              extraClasses='w-full'
              options={
                values.lossType
                  ? LossReasonAndTypeOptions.find((obj) => obj[values.lossType])[values.lossType]
                  : []
              }
              onChange={(e) => {
                setFieldValue('lossReason', e.target.value)
                setFieldValue('subLossReasons', [])
                setTimeout(() => {
                  validateForm()
                }, 0)
              }}
            />
            {values.lossReason &&
              values.lossReason in subLossReasonsOptions &&
              values.lossReason !== 'other' && (
                <>
                  <MultiSelectField
                    label='Select sub loss reasons'
                    name='subLossReasons'
                    multiple
                    options={values.lossReason ? subLossReasonsOptions[values.lossReason] : []}
                    onChange={(e) => {
                      setFieldValue(
                        'subLossReasons',
                        e.map((item) => item.value)
                      )
                      setTimeout(() => {
                        validateForm()
                      }, 0)
                    }}
                  />
                </>
              )}
            {values.lossReason && values.lossReason === 'other' && (
              <div className='mt-2'>
                <InputField
                  style='default'
                  name='otherText'
                  placeholder=''
                  // as='textarea'

                  rows={4}
                  showError={true}
                  onChange={(e) => {
                    setFieldValue('otherText', e.target.value)
                    setTimeout(() => {
                      validateForm()
                    }, 0)
                  }}
                />
              </div>
            )}
          </>
        ),
        disabledStep: !values.lossType,
        disabledButton: !values.lossReason,
        hideButton:
          !(values.lossReason == 'credit' || values.lossReason == 'bankruptcy') &&
          mode === 'closed-loss',
        buttonLabel: 'Next',
      },
    ]
    if (
      (values.lossReason == 'credit' || values.lossReason == 'bankruptcy') &&
      mode === 'closed-loss'
    )
      steps.push({
        title: 'AAN Denial Reasons',
        content: () => (
          <ReactSelect
            defaultValue={getDefaultValues()}
            className='cursor-pointer'
            autoFocus
            isMulti
            placeholder='Select AAN Denial Reasons'
            hideSelectedOptions={false}
            options={Object.keys(AANDenialReasons).map((reason) => {
              return { value: reason, label: AANDenialReasons[reason] }
            })}
            name='selectedAANDenialReasons'
            onChange={(e: any) => {
              setFieldValue(
                'selectedAANDenialReasons',
                e.map((aanValues) => {
                  return aanValues.value
                })
              )
              setTimeout(() => {
                validateForm()
              }, 0)
            }}
          />
        ),
        buttonLabel: 'Next',
        hideButton: true,
      })

    if (mode === 'uam-decline')
      steps.push({
        title: 'Add Commentary',
        content: () => (
          <InputField
            style='default'
            name='uamCommentary'
            placeholder=''
            as='textarea'
            customClasses='!textarea !textarea-bordered w-full'
            rows={4}
            showError={true}
            onChange={(e) => {
              setFieldValue('uamCommentary', e.target.value)
              setTimeout(() => {
                validateForm()
              }, 0)
            }}
          />
        ),
        buttonLabel: 'Next',
        hideButton: true,
      })
    return steps
  }

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        lossType: closedLossDrawerType,
        lossReason: closedLossDrawerReason,
        selectedAANDenialReasons: aanReasons,
        subLossReasons: closedLossDrawerSubReasons,
        otherText: closedLossDrawerSubReasonOtherText,
        uamCommentary: '',
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        lossType: Yup.string().required(fieldIsRequiredMessage),
        lossReason: Yup.string().required(fieldIsRequiredMessage),
        subLossReasons: Yup.array().when('lossReason', {
          is: (lossReason) => lossReason in subLossReasonsOptions && lossReason !== 'other',
          then: Yup.array()
            .min(1, 'subLossReasons field must have at least 1 item')
            .required(fieldIsRequiredMessage),
          otherwise: Yup.array(),
        }),
        otherText: Yup.string().when('lossReason', {
          is: (lossReason) => lossReason === 'other',
          then: Yup.string().required(fieldIsRequiredMessage),
        }),
        selectedAANDenialReasons: Yup.array().when('lossReason', {
          is: (lossReason: string) =>
            (lossReason == 'credit' || lossReason == 'bankruptcy') && mode === 'closed-loss',
          then: () => Yup.array().min(1).required(fieldIsRequiredMessage),
        }),
      })}
      validateOnChange={true}
      validateOnBlur={false}
      onSubmit={mode === 'closed-loss' ? handleClosedLoss : handleUamDecline}
    >
      {({ values, setFieldValue, isValid, validateForm, isSubmitting, handleSubmit }) => {
        return (
          <>
            <Stepper
              initialStep={initialStep}
              steps={getContent(values, setFieldValue, validateForm)}
              finishButton={{
                label: isError ? 'Retry - Select Reason' : 'Finish - Select Reason',
                onClick: handleFinishAction(handleSubmit),
                extraDisabled: isSubmitting || !isValid,
                footerMessage: message && (
                  <div className={`${isError && 'text-red-500'}`}>{message}</div>
                ),
                loadingFooter: isSubmitting,
              }}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default memo(ClosedLost)
